/**
 * DecisionSections — sekcje kartotek kokpitu decyzji („Grupuj według: Kartoteka", zakresy „Moje
 * czekające", „Przeterminowane" i „Zespół").
 *
 * Każda kartoteka z zestawem sekcji (`SECTION_KITS` — kartoteki z katalogu pól, stałe zestawy
 * dokumentów Symfonii Handel z `handel-section-kits.tsx`, albo kartoteki własne `custom.<slug>` z
 * `custom-section-kit.tsx`) dostaje OSOBNĄ SEKCJĘ z kolumnami listy tej kartoteki
 * (`DecisionSectionTable`) zamiast zwijanej grupy wspólnej tabeli. Sekcja startuje
 * ZWINIĘTA; dane wczytują się dopiero po rozwinięciu. Nagłówek: nazwa kartoteki + licznik REKORDÓW
 * (`recordCount` z nagłówka grupy — liczba wierszy w tabeli sekcji, nie kroków) + „Wartość" całej
 * sekcji (`sumsByCurrency` z nagłówka grupy, osobno dla każdej waluty — bez niej, gdy kartoteka nie
 * ma kwot). Zwinięcie sekcji
 * odmontowuje tabelę (zatrzymuje też jej auto-odświeżanie, zdejmuje jej kroki z rejestru zakładki i z
 * zaznaczenia); stan sortowania, filtrów i strony nie jest zapamiętywany między rozwinięciami, więc
 * nie ma czego zachować także z zaznaczenia (patrz `DecisionSectionTable`).
 *
 * Tryb widoku („Lista / Karty"), zaznaczanie i zakres (`scope`) przychodzą od zakładki i idą do
 * tabel sekcji bez zmian — patrz `DecisionSectionTable`.
 */

import { type ReactElement, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ViewMode } from '@saas/shared';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { Button } from '@/components/Button';
import type { DecisionTarget } from '@/components/processes/DecisionButtons';
import type {
  MyPendingDecisionRow,
  PendingDecisionGroupHeader,
  PendingDecisionScope,
} from '@/lib/processes/approvals-api';
import { formatMoney } from '@/lib/processes/format-money';

import { groupLabelText } from '../InstanceListingTable';

import { getCustomSectionKit } from './custom-section-kit';
import { DecisionSectionTable } from './DecisionSectionTable';
import { getStaticSectionKit } from './handel-section-kits';
import { getSectionKit, type SectionKit } from './section-kits';
import type { DecisionSectionSelection } from './selection';

/** Nagłówek koszyka kartoteki razem z jej zestawem sekcji. */
export interface SectionEntry {
  header: PendingDecisionGroupHeader;
  kit: SectionKit;
}

/**
 * Dzieli nagłówki koszyków „Kartoteka" na te, które mają sekcję (w kolejności z serwera), i resztę
 * (klucze bez żadnego zestawu — nie powinny się zdarzyć — zostają zwijanymi grupami wspólnej tabeli
 * jako zabezpieczenie). Sekcję mają kartoteki z katalogu pól (klasy dokumentów, kontrahenci),
 * dokumenty Symfonii Handel i kartoteki własne (`custom.<slug>`).
 */
export function splitSectionGroups(groups: readonly PendingDecisionGroupHeader[]): {
  sections: SectionEntry[];
  rest: PendingDecisionGroupHeader[];
} {
  const sections: SectionEntry[] = [];
  const rest: PendingDecisionGroupHeader[] = [];
  for (const header of groups) {
    const kit =
      getSectionKit(header.key) ??
      getStaticSectionKit(header.key) ??
      getCustomSectionKit(header.key);
    if (kit === undefined) rest.push(header);
    else sections.push({ header, kit });
  }
  return { sections, rest };
}

export interface DecisionSectionsProps {
  /** Sekcje do wyrenderowania — w kolejności z serwera (`splitSectionGroups`). */
  sections: readonly SectionEntry[];
  /** Zakres „Przeterminowane" (`?overdue=true`) — przekazywany do zapytań sekcji. */
  overdue: boolean;
  /**
   * Zakres: `team` = decyzje podwładnych (`?scope=team`) — sekcje tylko do odczytu (bez przycisków
   * decyzji i zaznaczania). Domyślnie `mine`.
   */
  scope?: PendingDecisionScope;
  /** Interwał auto-odświeżania w ms albo `false` (decyduje rodzic — wspólna pauza przy oknie decyzji). */
  refetchInterval: number | false;
  /** Otwiera okno „Akceptacja" dla kroku (rodzic renderuje okno — to samo co dla wspólnej listy). */
  onOpenDecision: (row: MyPendingDecisionRow, target: DecisionTarget) => void;
  /** Wspólny tryb widoku „Lista / Karty" (wybór trzyma rodzic). Domyślnie lista. */
  viewMode?: ViewMode;
  /** Udział we wspólnym zaznaczeniu rodzica; brak = sekcje bez zaznaczania. */
  selection?: DecisionSectionSelection;
}

interface DecisionSectionProps extends Omit<DecisionSectionsProps, 'sections'> {
  entry: SectionEntry;
}

function DecisionSection({
  entry: { header, kit },
  overdue,
  scope = 'mine',
  refetchInterval,
  onOpenDecision,
  viewMode = 'list',
  selection,
}: DecisionSectionProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const name = groupLabelText(t, 'target_class', header);
  const valueId = useId();
  const hasValue = header.sumsByCurrency.length > 0;

  return (
    <section className="flex flex-col gap-2" data-testid={`approvals-section-${header.key}`}>
      <Button
        variant="ghost"
        className="w-full justify-start rounded-xl bg-bg-tertiary hover:bg-bg-secondary"
        aria-expanded={expanded}
        // Nazwa dostępna = sama nazwa sekcji i licznik rekordów; „Wartość" (kwoty w kilku walutach) wydłużałaby
        // ją o kilkanaście słów, więc czytnik ekranu dostaje ją jako OPIS przycisku, a nie część nazwy.
        aria-label={t('tenant.processes.approvals.sections.toggleAria', {
          name,
          count: header.recordCount,
        })}
        aria-describedby={hasValue ? valueId : undefined}
        onClick={() => setExpanded((prev) => !prev)}
        leftIcon={
          expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-text-muted" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-text-muted" aria-hidden="true" />
          )
        }
        data-testid={`approvals-section-toggle-${header.key}`}
      >
        <span className="font-medium text-text-primary">{name}</span>
        <span
          className="rounded-full bg-bg-secondary px-2 py-0.5 text-xs font-semibold text-text-secondary"
          data-testid={`approvals-section-count-${header.key}`}
        >
          {header.recordCount}
        </span>
        {/* „Wartość" całej sekcji — suma z nagłówka koszyka, osobno dla każdej waluty (jak dawna grupa).
            Kartoteki bez kwot dostają pustą listę, więc nie mają tego pola. */}
        {hasValue && (
          <span
            id={valueId}
            className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-text-secondary"
            data-testid={`approvals-section-value-${header.key}`}
          >
            <span className="text-text-tertiary">
              {t('tenant.processes.approvals.sections.valueLabel')}
            </span>
            {header.sumsByCurrency.map((sum) => (
              <span
                key={sum.currencyCode}
                className="font-medium tabular-nums"
                data-testid={`approvals-section-value-${header.key}-${sum.currencyCode}`}
              >
                {formatMoney(sum.total, sum.currencyCode)}
              </span>
            ))}
          </span>
        )}
      </Button>
      {expanded && (
        <DecisionSectionTable
          groupKey={header.key}
          kit={kit}
          overdue={overdue}
          scope={scope}
          refetchInterval={refetchInterval}
          onOpenDecision={onOpenDecision}
          viewMode={viewMode}
          selection={selection}
        />
      )}
    </section>
  );
}

export function DecisionSections({
  sections,
  overdue,
  scope,
  refetchInterval,
  onOpenDecision,
  viewMode,
  selection,
}: DecisionSectionsProps): ReactElement {
  return (
    <div className="flex shrink-0 flex-col gap-3" data-testid="approvals-sections">
      {sections.map((entry) => (
        <DecisionSection
          key={entry.header.key}
          entry={entry}
          overdue={overdue}
          scope={scope}
          refetchInterval={refetchInterval}
          onOpenDecision={onOpenDecision}
          viewMode={viewMode}
          selection={selection}
        />
      ))}
    </div>
  );
}
